import { formatNumber } from '@/utils/formatNumber'
import { SkeletonCircle } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer, Sector } from 'recharts'

interface Payload {
  name: string
  value: number
  fill: string
}

export interface DecompositionChartProps {
  isLoading?: boolean
  width?: string | number
  height?: string | number
  data: Payload[]
}

const renderActiveShape = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent } = props

  return (
    <g>
      <circle cx={cx} cy={cy} r={innerRadius} fill='var(--ce-colors-chakra-body-bg)' stroke='var(--ce-colors-chakra-border)' strokeWidth={2} />
      <ActiveShape name={payload.name} percent={percent} />
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        cornerRadius={3}
        fill={fill}
        strokeWidth={2}
        stroke={fill}
      />

    </g>
  )
}

const ActiveShape = ({ name, percent }: {
  name: string
  percent: number
}) => {

  return (
    <g>
      <text x={60} y={60 - 20} dy={24} textAnchor="middle" fill='var(--ce-colors-chakra-body-text)' fontWeight='var(--ce-fontWeights-medium)' fontSize='var(--ce-fontSizes-3xl)'>
        {formatNumber(percent * 100, { maximumFractionDigits: 0 })} %
      </text>
      <text x={60} y={60 + 16} dy={8} textAnchor="middle" fill='var(--ce-colors-chakra-body-text)' fontWeight='var(--ce-fontWeights-normal)' fontSize='var(--ce-fontSizes-sm)'>
        {name}
      </text>
    </g>
  )
}


export const DecompositionChart = ({ isLoading, width = '100%', height = '100%', data }: DecompositionChartProps) => {
  const [showDefaultActiveShape, setShowDefaultActiveShape] = useState(false)

  useEffect(() => {
    if (isLoading) {
      setShowDefaultActiveShape(false)
      return
    }
  }, [isLoading])

  const animationDuration = 800

  const largestPortion = data.reduce((max, item) => item.value > max.value ? item : max, data[0])

  const totalValue = data.reduce((sum, item) => sum + item.value, 0)

  return <SkeletonCircle isLoaded={!isLoading} width={width} height={height}>
    <ResponsiveContainer width="100%" height="100%">
      <PieChart width={100} height={100}>
        {showDefaultActiveShape && (
          <ActiveShape
            name={largestPortion.name}
            percent={largestPortion.value / totalValue}
          />
        )}
        <Pie
          activeShape={renderActiveShape}
          data={data}
          dataKey="value"
          innerRadius='83%'
          outerRadius='100%'
          cornerRadius='3'
          paddingAngle={2}
          animationBegin={0}
          animationDuration={animationDuration}
          onAnimationEnd={() => setTimeout(() => {
            setShowDefaultActiveShape(true)
          }, animationDuration)}
          startAngle={90}
          endAngle={-270}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.fill} stroke={entry.fill} strokeWidth={0} />
          ))}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  </SkeletonCircle>
}
